import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { RadioButton } from 'primereact/radiobutton';
import { stanjeRobeProductsAPI, stanjeRobeStockInAPI } from '../services/api';
import { StanjeRobeProduct, StanjeRobeStockInRecord } from '../types';
import { showCustomAlert } from './CustomAlert';
import StanjeRobeProductM2Info from './StanjeRobeProductM2Info';
import { computeM2FromQuantity, conversionBreakdown, formatM2 } from '../utils/stanjeRobeCalc';

const fmtDate = (iso?: string) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('sr-RS'); } catch { return iso; }
};

const StanjeRobeStockIn: React.FC = () => {
  const [records, setRecords] = useState<StanjeRobeStockInRecord[]>([]);
  const [products, setProducts] = useState<StanjeRobeProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(true);
  const [form, setForm] = useState({
    productId: '',
    quantity: 0,
    entryDate: new Date(),
    notes: ''
  });

  const productOptions = useMemo(
    () => products.map((p) => ({ label: `${p.brandName} – ${p.name}`, value: p._id })),
    [products]
  );

  const selectedProduct = useMemo(() => products.find((p) => p._id === form.productId), [products, form.productId]);

  const calculatedM2 = useMemo(
    () => computeM2FromQuantity(form.quantity, 'paleta', selectedProduct),
    [form.quantity, selectedProduct]
  );

  const conversionText = useMemo(
    () => conversionBreakdown(form.quantity, 'paleta', selectedProduct),
    [form.quantity, selectedProduct]
  );

  const loadProducts = async () => {
    const data = await stanjeRobeProductsAPI.getAll();
    setProducts(Array.isArray(data) ? data : []);
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const data = await stanjeRobeStockInAPI.getAll({ showAll });
      setRecords(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadProducts(); }, []);
  useEffect(() => { void loadRecords(); }, [showAll]);

  const openCreate = () => {
    setForm({ productId: '', quantity: 0, entryDate: new Date(), notes: '' });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.productId) { setError('Odaberite robu'); return; }
    if (!form.quantity || form.quantity <= 0) { setError('Količina mora biti veća od 0'); return; }
    try {
      await stanjeRobeStockInAPI.create({
        productId: form.productId,
        quantity: form.quantity,
        unit: 'paleta',
        entryDate: form.entryDate.toISOString(),
        notes: form.notes
      });
      setDialogVisible(false);
      await loadRecords();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri unosu');
    }
  };

  const handleDelete = (rec: StanjeRobeStockInRecord) => {
    showCustomAlert.confirm('Obrisati ovaj ulaz na stanje?', async () => {
      try {
        await stanjeRobeStockInAPI.delete(rec._id);
        await loadRecords();
      } catch (err: any) {
        showCustomAlert.error(err?.response?.data?.message || 'Greška');
      }
    });
  };

  return (
    <div className="p-3 md:p-4">
      <Card title="Ulaz na stanje" subTitle="Unos proizvodnje u paletama (automatski preračun u m²)">
        {error && <Message severity="error" text={error} className="mb-3" />}
        <div className="flex flex-wrap gap-3 align-items-center mb-3">
          <div className="flex align-items-center gap-2">
            <RadioButton inputId="all" checked={showAll} onChange={() => setShowAll(true)} />
            <label htmlFor="all">Svi zapisi</label>
          </div>
          <div className="flex align-items-center gap-2">
            <RadioButton inputId="today" checked={!showAll} onChange={() => setShowAll(false)} />
            <label htmlFor="today">Samo danas</label>
          </div>
          <Button label="Novi ulaz" icon="pi pi-plus" onClick={openCreate} className="ml-auto" disabled={!products.length} />
        </div>
        <DataTable value={records} loading={loading} paginator rows={15} emptyMessage="Nema zapisa.">
          <Column field="brandName" header="Brend" sortable />
          <Column field="productName" header="Roba" sortable />
          <Column header="Količina" body={(r: StanjeRobeStockInRecord) => `${r.quantity} ${r.unit}`} />
          <Column header="m²" body={(r: StanjeRobeStockInRecord) => (r.m2Total ?? 0).toFixed(2)} />
          <Column header="Datum" body={(r: StanjeRobeStockInRecord) => fmtDate(r.entryDate)} />
          <Column field="notes" header="Napomena" />
          <Column header="" body={(r: StanjeRobeStockInRecord) => (
            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => handleDelete(r)} />
          )} style={{ width: 60 }} />
        </DataTable>
      </Card>

      <Dialog header="Novi ulaz na stanje" visible={dialogVisible} onHide={() => setDialogVisible(false)} style={{ width: '560px' }}>
        <div className="p-fluid">
          <div className="field">
            <label>Roba *</label>
            <Dropdown value={form.productId} options={productOptions} onChange={(e) => setForm({ ...form, productId: e.value })} filter placeholder="Odaberite..." className="w-full" />
          </div>
          <StanjeRobeProductM2Info product={selectedProduct} />
          <div className="field">
            <label>Datum</label>
            <Calendar value={form.entryDate} onChange={(e) => setForm({ ...form, entryDate: (e.value as Date) || new Date() })} dateFormat="dd.mm.yy" showIcon className="w-full" />
          </div>
          <div className="formgrid grid">
            <div className="field col-6">
              <label>Količina (paleta) *</label>
              <InputNumber value={form.quantity} onValueChange={(e) => setForm({ ...form, quantity: e.value ?? 0 })} min={0} maxFractionDigits={3} />
            </div>
            <div className="field col-6">
              <label>Jedinica</label>
              <InputText value="paleta" disabled />
            </div>
          </div>
          <div className="field">
            <label>Kvadrati (m²)</label>
            <div className="p-inputtext p-component w-full" style={{ opacity: 0.9 }}>
              {calculatedM2 > 0 ? `${formatM2(calculatedM2)} m²` : '—'}
            </div>
            {conversionText && (
              <div className="p-message p-message-info" style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem' }}>
                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }} />
                Kalkulisano: {conversionText}
              </div>
            )}
          </div>
          <div className="field">
            <label>Napomena</label>
            <InputTextarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} autoResize />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" outlined severity="secondary" onClick={() => setDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default StanjeRobeStockIn;
